import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, RefreshCw } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { DemoDetail } from "./DemoDetail";
import { SignDialog } from "./SignDialog";
import { BilanCommercial } from "./BilanCommercial";

const ORDER = ["demande", "contacte", "planifiee", "realisee", "devis", "signe", "perdu"];
const TOP = { demande: "border-t-[#7DC4E8]", contacte: "border-t-sky-500", planifiee: "border-t-[#0369A1]", realisee: "border-t-indigo-500", devis: "border-t-amber-500", signe: "border-t-emerald-500", perdu: "border-t-slate-400" };
export const fcfa = (n) => `${(n || 0).toLocaleString("fr-FR").replace(/\u202f|\u00a0/g, " ")} FCFA`;
export const dateFr = (s) => (s ? new Date(s).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

function DemoCard({ d, onOpen }) {
  return (
    <div role="button" tabIndex={0} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", d.id)} onClick={() => onOpen(d)} onKeyDown={(e) => e.key === "Enter" && onOpen(d)} data-testid={`demo-card-${d.id}`}
      className="w-full cursor-grab rounded-xl border border-[#7DC4E8] bg-white p-3 text-left shadow-sm transition-transform duration-150 hover:-translate-y-0.5 active:cursor-grabbing">
      <p className="truncate text-sm font-semibold text-navy">{d.establishment || "—"}</p>
      <p className="truncate text-xs text-slate-500">{d.responsable} · {d.offre}</p>
      {d.demo_at && <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#E0F2FE] px-2 py-0.5 text-[11px] font-medium text-[#0369A1]"><CalendarClock size={11} /> {dateFr(d.demo_at)}</p>}
      {d.montant > 0 && <p className="mt-1 text-xs font-semibold text-[#0C4A6E]">{fcfa(d.montant)} HT</p>}
    </div>
  );
}

export function DemosPipeline() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [over, setOver] = useState("");
  const [signing, setSigning] = useState(null);
  const [rk, setRk] = useState(0);
  const load = useCallback(() => { setRk((k) => k + 1); return api.get("/admin/demos").then(({ data }) => setData(data)).catch((e) => { toast.error(apiError(e, "Chargement impossible")); setData({ stages: {}, demos: [] }); }); }, []);
  useEffect(() => { load(); }, [load]);

  const replace = (d) => { setRk((k) => k + 1); setData((s) => ({ ...s, demos: s.demos.map((x) => (x.id === d.id ? d : x)) })); setOpen((o) => (o && o.id === d.id ? d : o)); };
  const move = async (id, stage) => {
    const cur = data.demos.find((x) => x.id === id);
    if (!cur || cur.pipeline_stage === stage) return;
    if (stage === "signe") { setSigning(cur); return; }
    try { const { data: d } = await api.patch(`/admin/demos/${id}`, { pipeline_stage: stage }); replace(d); toast.success(`Déplacée : ${data.stages[stage]}`); }
    catch (e) { toast.error(apiError(e, "Déplacement impossible")); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ciel" /></div>;
  const signe = data.demos.filter((d) => d.pipeline_stage === "signe");
  return (
    <div data-testid="demos-pipeline">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-navy">Suivi des démos</h2>
          <p className="text-xs text-slate-500">{data.demos.length} demande{data.demos.length > 1 ? "s" : ""} · {signe.length} contrat{signe.length > 1 ? "s" : ""} signé{signe.length > 1 ? "s" : ""} ({fcfa(signe.reduce((a, d) => a + (d.montant || 0), 0))} HT). Glissez une carte pour changer d'étape.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="demos-reload" className="rounded-full"><RefreshCw size={14} className="mr-1.5" /> Actualiser</Button>
      </div>
      <BilanCommercial refreshKey={rk} />
      <div className="mt-4 flex gap-3 overflow-x-auto pb-3">
        {ORDER.map((s) => {
          const items = data.demos.filter((d) => d.pipeline_stage === s);
          return (
            <div key={s} onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver("")} onDrop={(e) => { e.preventDefault(); setOver(""); move(e.dataTransfer.getData("text/plain"), s); }}
              data-testid={`demos-col-${s}`} className={`flex w-64 shrink-0 flex-col rounded-2xl border-t-4 ${TOP[s]} p-3 transition-colors ${over === s ? "bg-[#BAE6FD]" : "bg-[#E0F2FE]"}`}>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-[#0C4A6E]">{data.stages[s]}</p>
                <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-[#0369A1]" data-testid={`demos-count-${s}`}>{items.length}</span>
              </div>
              <div className="flex min-h-[80px] flex-col gap-2">{items.map((d) => <DemoCard key={d.id} d={d} onOpen={setOpen} />)}</div>
            </div>
          );
        })}
      </div>
      <DemoDetail demo={open} stages={data.stages} order={ORDER} onClose={() => setOpen(null)} onSaved={replace} onSign={setSigning} />
      <SignDialog key={signing?.id || "none"} demo={signing} onClose={() => setSigning(null)} onSaved={replace} />
    </div>
  );
}
